import { allLangs, defaultLang } from '../config';

// ----------------------------------------------------------------------

export const SUPPORTED_LANGUAGES = allLangs.map(l => l.value);

/**
 * Extract language code from pathname
 * @param {string} pathname - URL pathname (e.g., "/en/blog")
 * @returns {string|null} - Language code or null if not found
 */
export function getLanguageFromPath(pathname) {
  const parts = pathname.split('/').filter(Boolean);
  const potentialLang = parts[0];
  return isValidLanguage(potentialLang) ? potentialLang : null;
}

/**
 * Remove language prefix from pathname
 * @param {string} pathname - URL pathname (e.g., "/en/blog")
 * @returns {string} - Pathname without language (e.g., "/blog")
 */
export function stripLanguageFromPath(pathname) {
  const parts = pathname.split('/').filter(Boolean);
  if (isValidLanguage(parts[0])) {
    parts.shift(); // Remove language prefix
  }
  return `/${parts.join('/')}`;
}

/**
 * Add language prefix to pathname
 * @param {string} path - Pathname without language (e.g., "/blog")
 * @param {string} lang - Language code (e.g., "en")
 * @returns {string} - Pathname with language prefix (e.g., "/en/blog")
 */
export function addLanguageToPath(path, lang) {
  const cleanPath = stripLanguageFromPath(path);
  const finalPath = cleanPath.startsWith('/') ? cleanPath.slice(1) : cleanPath;
  const result = `/${lang}/${finalPath}`.replace(/\/+$/, '');
  return result || `/${lang}`;
}

/**
 * Get same pathname but in different language
 * @param {string} pathname - Current pathname (e.g., "/en/blog")
 * @param {string} newLang - New language code (e.g., "es")
 * @returns {string} - New pathname (e.g., "/es/blog")
 */
export function getCurrentLanguagePath(pathname, newLang) {
  const pathWithoutLang = stripLanguageFromPath(pathname);
  return addLanguageToPath(pathWithoutLang, newLang);
}

/**
 * Validate if language code is supported
 * @param {string} lang - Language code to validate
 * @returns {boolean} - True if language is supported
 */
export function isValidLanguage(lang) {
  return SUPPORTED_LANGUAGES.includes(lang);
}

/**
 * Detect browser language with fallback to default
 * @returns {string} - Detected language code
 */
export function detectBrowserLanguage() {
  // Try to get language from navigator
  const browserLang = navigator.language.split('-')[0]; // "en-US" -> "en"

  // Return if valid, otherwise return default
  return isValidLanguage(browserLang) ? browserLang : defaultLang.value;
}
